/* Photo Motif — sticky CTA visibility.
   Vanilla, no dependencies.

   The bar exists to offer the primary action when none is on screen. It watches
   every primary CTA on the page with an IntersectionObserver and shows itself
   only while all of them are out of view. "Every primary CTA" is a question
   about what the customer can see and press, not about who carries the class —
   see pickerIsPrimary().

   The bar is hidden in the HTML and only revealed here, so a visitor without
   JavaScript never sees two identical buttons stacked on top of each other.
   Hidden means hidden: aria-hidden, no tab stop, no pointer events — not just
   opacity 0.

   Three jobs were added after the bar was measured on real phones:

   · THE CLEARANCE IS MEASURED, NOT ASSUMED. See occlusion().
   · ON THE ORDER PAGE THE BAR IS NOT A BUY BUTTON. See shortcut().
   · THE PHOTO PICKER IS ONE OF THE CTAS IT STANDS DOWN FOR, while it is the
     one wearing terracotta. See pickerIsPrimary() and syncTargets(). */

(function () {
  'use strict';

  /* Grace margin at the top so a CTA grazing the edge does not make the bar
     flicker. There is no constant for the bottom any more, and that is the
     point — see occlusion(). */
  var EDGE_TOP = 24;

  var teardowns = [];

  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* scrollIntoView's object argument is refused by old engines, which throw
     rather than ignore it. The no-argument call lands in the right place, just
     without the framing. */
  function bring(el, smooth) {
    if (!el || !el.scrollIntoView) return;
    try {
      el.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
    } catch (error) {
      el.scrollIntoView();
    }
  }

  function setup(inner) {
    var bar = inner.closest('.pm-sticky-cta') || inner.parentElement;
    if (!bar) return;

    var button = inner.querySelector('.pm-sticky__btn');
    var focusables = bar.querySelectorAll('a[href], button, input, select, textarea');

    /* Every primary CTA on the page but the bar's own. Fixed for the life of
       the page; the picker joins and leaves the observed set around it, which
       is why that set is assembled separately. See syncTargets(). */
    var primaries = Array.prototype.filter.call(
      document.querySelectorAll('.pm-btn--primary'),
      function (el) {
        return !bar.contains(el);
      }
    );

    /* The order block, found by its control rather than by the template name,
       for the reason shortcut() gives.

       The picker is the <input>, which is visually hidden; the <label> around
       it is the face the customer sees and presses. Scrolling can fall back to
       the input, which lands in the same place. Watching cannot: .pm-visually-
       hidden is a 1px box at the same coordinates, so it would report itself on
       screen while there is nothing there to see. No label, no face. */
    var picker = document.querySelector('[data-pm-upload-input]');
    var flow = picker && picker.closest('[data-pm-upload]');
    var face = (picker && picker.closest('label')) || null;

    var targets = [];

    bar.classList.add('is-ready');

    /* null rather than false: the first reveal() has to run, whichever way it
       goes, because it is what puts aria-hidden and the tab stops in step with
       the class the server rendered. */
    var visible = null;
    var observer = null;
    var observedClearance = -1;
    var onScreen = new Set();
    var frame = 0;

    function reveal(next) {
      if (next === visible) return;
      visible = next;

      bar.classList.toggle('is-visible', next);

      if (next) bar.removeAttribute('aria-hidden');
      else bar.setAttribute('aria-hidden', 'true');

      Array.prototype.forEach.call(focusables, function (el) {
        if (next) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });

      /* What the bar covers has just changed, so what counts as off screen has
         changed with it. */
      syncObserver();
    }

    /* HOW MUCH OF THE VIEWPORT THE BAR IS ACTUALLY HIDING, right now.

       This replaces a hard-coded `EDGE_BOTTOM = 128`, which told the observer
       to discount the bottom 128px of every viewport whether the bar was up or
       not. A CTA was therefore judged off screen as soon as its top fell below
       innerHeight - 128 — so on any screen shorter than 726px the hero button,
       in plain sight, was declared invisible and the bar rose to cover it. iOS
       Safari gives a 390x844 iPhone about 664px of page and an iPhone SE 553,
       which is to say: every iPhone, on the first painted screen.

       A hidden bar hides nothing, so its clearance is zero until it is up. When
       it is up, the height is measured rather than remembered, because it
       changes — the reassurance line wraps at some widths, and the bar lays
       itself out as a row above 900px. */
    function occlusion() {
      return visible ? bar.offsetHeight : 0;
    }

    function onEntries(entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      });
      reveal(onScreen.size === 0);
    }

    /* rootMargin is fixed when an observer is built, so changing the clearance
       means building another one.

       THIS SETTLES, IT DOES NOT OSCILLATE. Raising the clearance shrinks the
       root, which can only take targets out of `onScreen`, which can only keep
       the bar up; dropping it to zero grows the root, which can only add
       targets, which can only keep the bar down. Each rebuild therefore agrees
       with the decision that caused it, and stops.

       `onScreen` deliberately survives the rebuild. A fresh observer reports
       every target again, but not until the next frame, and clearing the set
       would mean a moment of "no CTA anywhere" in between — which is the one
       state that flashes the bar on. */
    function syncObserver() {
      if (!targets.length || !('IntersectionObserver' in window)) return;

      var clearance = occlusion();
      if (observer && clearance === observedClearance) return;
      observedClearance = clearance;

      if (observer) observer.disconnect();
      observer = new IntersectionObserver(onEntries, {
        rootMargin: '-' + EDGE_TOP + 'px 0px -' + clearance + 'px 0px',
        threshold: 0,
      });
      targets.forEach(function (el) {
        observer.observe(el);
      });
    }

    /* WHAT COUNTS AS A PRIMARY CTA IS NOT ONLY WHAT CARRIES THE CLASS.

       On the order page the page's primary action is the photo picker, and it
       is a <label> wearing terracotta rather than a .pm-btn. A set built from
       `.pm-btn--primary` alone therefore left the bar standing while that
       control was whole on screen: two identical full-width rust buttons
       reading the same words two hundred pixels apart, the bar's ninety-three
       covering a seventh of a 664px phone at the moment the customer was
       reading the privacy note beside the picker. The rule this bar documents
       is "up only while all primary CTAs are out of view". The picker is one.

       AND ONLY WHILE IT IS ONE. Once a photograph is in, the picker turns pale
       and becomes the way to change your mind, the buy button below it comes
       live, and terracotta is on exactly one of the two — the rule
       product-order.liquid keeps. Counting a pale picker would stand the bar
       down over the thousand pixels between it and "Add to cart" and leave a
       screen and a half of proof with nothing to press, which is the fault the
       bar exists to answer. `data-pm-upload-preview` is the flag that decides
       the picker's face in CSS, so it decides its membership here: one flag,
       and the two cannot drift apart. */
    function pickerIsPrimary() {
      return !!face && !(flow && flow.hasAttribute('data-pm-upload-preview'));
    }

    /* Rebuilds the set, and the observer with it.

       `observedClearance` is reset because syncObserver's shortcut compares
       clearances, and the set can change while the clearance does not.

       Before the first reveal() this only loads the set. setup() has not
       decided anything yet, and an observer that has reported nothing looks
       exactly like "no CTA anywhere" — the one state that flashes the bar on. */
    function syncTargets() {
      targets = pickerIsPrimary() ? primaries.concat([face]) : primaries;

      var dropped = [];
      onScreen.forEach(function (el) {
        if (targets.indexOf(el) === -1) dropped.push(el);
      });
      dropped.forEach(function (el) {
        onScreen.delete(el);
      });

      if (visible === null) return;

      observedClearance = -1;
      syncObserver();

      /* Only a target that LEFT needs the decision made for it, because it can
         no longer report itself gone. One that has just JOINED will report
         itself a frame from now, and answering on its behalf first would read
         an empty `onScreen` and flash the bar on over the very control it is
         about to stand down from. */
      if (dropped.length) reveal(onScreen.size === 0);
    }

    /* THE ORDER PAGE IS THE ONE PAGE WHERE THE BAR IS NOT A BUY BUTTON.

       Found by the control rather than by the template name, so the bar follows
       the order block if it is ever placed on another page — and so this file
       needs no list to keep in step with the section's.

       Why it is not simply a copy of "Add to cart": that button is rendered
       disabled until the photograph has reached the studio backend and come
       back with an id (CONTRACT.md §5.3). A bar that duplicated it would offer
       a control that does nothing; a bar that looked enabled would be saying
       the order is ready when it is not. The step that is actually blocking is
       the picker, so the bar goes there. Once a photograph is in, there is
       something to come back to, and it says so instead. */
    function shortcut() {
      if (!picker || !button) return null;

      var pick = (inner.getAttribute('data-pm-sticky-pick-label') || '').trim();
      var resume = (inner.getAttribute('data-pm-sticky-resume-label') || '').trim();
      /* An empty label is the setting turned off, not an empty button. */
      if (!pick || !resume) return null;

      var submit = (flow || document).querySelector('[data-pm-upload-submit]');

      /* `empty` is the only state in which there is nothing to go back to.
         CONTRACT.md §5.3 names the other four. */
      function chosen() {
        var state = flow ? flow.getAttribute('data-pm-upload-state') : '';
        return !!state && state !== 'empty';
      }

      function syncLabel() {
        var text = chosen() ? resume : pick;
        if (button.textContent.trim() !== text) button.textContent = text;
      }

      function onClick(event) {
        if (event.defaultPrevented) return;
        /* A modified click is a request for a new tab. Leave it alone. */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (typeof event.button === 'number' && event.button > 0) return;

        event.preventDefault();

        if (chosen()) {
          var live = submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true';
          bring(live ? submit : flow || face || picker, !reduceMotion());
          if (live) {
            try {
              submit.focus({ preventScroll: true });
            } catch (error) {
              submit.focus();
            }
          }
          return;
        }

        /* Straight to the file picker, and the scroll is instant on purpose:
           the operating system's own picker covers the page the moment it
           opens, so an animation behind it is spent on nobody. What matters is
           where the customer is standing when it closes — beside the preview of
           their own photograph, not five hundred pixels above it. */
        bring(face || picker, false);
        try {
          picker.focus({ preventScroll: true });
        } catch (error) {
          /* A browser that refuses to focus a visually hidden input still
             opens its picker on click, which is the part that matters. */
        }
        picker.click();
      }

      /* The href is a real destination, not a placeholder: if this handler ever
         fails, the link still takes the customer to the photo field. */
      if (picker.id) button.setAttribute('href', '#' + picker.id);
      button.addEventListener('click', onClick);

      var watcher = null;
      if ('MutationObserver' in window && flow) {
        watcher = new MutationObserver(syncLabel);
        watcher.observe(flow, { attributes: true, attributeFilter: ['data-pm-upload-state'] });
      }

      syncLabel();

      return function () {
        button.removeEventListener('click', onClick);
        if (watcher) watcher.disconnect();
      };
    }

    /* Load the set before anything reads it. */
    syncTargets();

    var unbind = shortcut();

    /* The picker's face follows the flow's flag, so the set it belongs to is
       rebuilt when that flag moves. A separate watcher from shortcut()'s, and
       on a separate attribute, because it answers a separate question: that
       one asks what the button should say, this one asks what is on screen. */
    var faceWatcher = null;
    if (face && flow && 'MutationObserver' in window) {
      faceWatcher = new MutationObserver(syncTargets);
      faceWatcher.observe(flow, { attributes: true, attributeFilter: ['data-pm-upload-preview'] });
    }

    /* The bar's height changes with the viewport — the reassurance line wraps,
       and the layout switches to a row at 900px — so the clearance is measured
       again rather than carried over. */
    function remeasure() {
      if (frame) return;
      frame = requestAnimationFrame(function () {
        frame = 0;
        observedClearance = -1;
        syncObserver();
      });
    }

    window.addEventListener('resize', remeasure);
    window.addEventListener('orientationchange', remeasure);

    teardowns.push(function () {
      if (observer) observer.disconnect();
      if (faceWatcher) faceWatcher.disconnect();
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('orientationchange', remeasure);
      if (unbind) unbind();
    });

    /* No primary CTA anywhere, or no observer support: the bar is the only
       way to act, so keep it up. */
    if (!targets.length || !('IntersectionObserver' in window)) {
      reveal(true);
      return;
    }

    /* Hidden until the observer says otherwise, which is also what builds it. */
    reveal(false);
  }

  function init() {
    while (teardowns.length) teardowns.pop()();
    document.querySelectorAll('[data-pm-sticky-cta]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: sections come and go, so the target list is rebuilt. */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
})();
